// Shared limits for the two attachment upload paths (video attachments and homework
// submission attachments). Both storage buckets are configured with the same 50MB
// file_size_limit and allowed_mime_types (migrations #44 and #96) — these constants
// mirror that config so the browser can reject a file before it spends minutes
// uploading only to get a 413 "Payload too large" back from the Storage API.

export const MAX_ATTACHMENT_SIZE = 52428800 // 50MB — matches the buckets' file_size_limit

// The Supabase client talks to the /api rewrite (vercel.json), and Vercel rejects any
// request body over 4.5MB with a 413 FUNCTION_PAYLOAD_TOO_LARGE before it reaches
// Storage. Measured: 4MB arrives, 4.5MB does not. Files above this threshold go straight
// to Supabase on a signed upload URL instead (see utils/attachmentUpload); smaller ones
// keep the proxied path. 4MB leaves room for the multipart envelope around the file.
export const PROXY_BODY_LIMIT = 4 * 1024 * 1024

export const ALLOWED_ATTACHMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain',
]

// Kept in step with MIME_BY_EXTENSION in supabase/functions/_shared/attachmentText.ts,
// which has to make the same guess when reading a stored attachment back.
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  txt: 'text/plain',
}

// Some browsers report an empty type for .pptx/.docx, and an untyped File is sent as
// application/octet-stream — which the buckets' allowed_mime_types rejects with a 415.
// Fall back to the extension so the file is stored under the type it actually is.
export function guessMimeType(file: File): string {
  const reported = file.type.split(';')[0].trim().toLowerCase()
  if (reported && reported !== 'application/octet-stream') return reported
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return MIME_BY_EXTENSION[extension] ?? ''
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Client-side validation before upload. Returns an error message, or null when OK.
export function validateAttachment(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `ファイルサイズが大きすぎます（${formatFileSize(file.size)} / 最大 ${formatFileSize(MAX_ATTACHMENT_SIZE)}）`
  }
  // guessMimeType recovers the type from the extension when the browser reports none;
  // an unrecognised extension leaves it blank, and the bucket's allowed_mime_types stays
  // the backstop rather than blocking a file the browser simply failed to describe.
  const mimeType = guessMimeType(file)
  if (mimeType && !ALLOWED_ATTACHMENT_TYPES.includes(mimeType)) {
    return 'この形式のファイルは添付できません'
  }
  return null
}

// The Storage API answers an over-sized body with HTTP 413, which surfaces as an opaque
// "Payload too large" (or nothing at all) in the console. Turn it into something a
// teacher or student can act on.
// Takes unknown because storage-js reports failures through two channels: an error it
// returns, and an exception it throws when the response never parsed.
export function describeUploadError(error: unknown): string {
  const err = (error ?? {}) as { message?: string; statusCode?: string | number }
  const status = err.statusCode !== undefined ? String(err.statusCode) : ''
  const message = err.message ?? ''
  if (status === '413' || /payload too large|exceeded the maximum allowed size/i.test(message)) {
    return `ファイルサイズが大きすぎます（最大 ${formatFileSize(MAX_ATTACHMENT_SIZE)}）`
  }
  if (status === '415' || /mime type|not supported/i.test(message)) {
    return 'この形式のファイルは添付できません'
  }
  // An upload that dies mid-flight (dropped connection, a network that blocks the
  // Storage host) rejects the fetch outright, with no status code to go on.
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(message)) {
    return 'ネットワークエラーによりアップロードできませんでした。通信環境をご確認のうえ、もう一度お試しください'
  }
  // storage-js reads the response as JSON before it looks at the status, so anything that
  // answers with a non-JSON error page — a Vercel deployment-protection redirect in front
  // of a preview deployment, a gateway error — surfaces as a bare parse error from the
  // browser ("The string did not match the expected pattern." in Safari) with nothing in
  // it about the upload. Over-sized bodies are the usual reason we never reach Storage.
  if (/did not match the expected pattern|unexpected (token|end of)|json parse|not valid json/i.test(message)) {
    return 'アップロードがサーバーに拒否されました（応答がJSONではありません）。ファイルサイズを小さくしてお試しください'
  }
  return message || 'アップロードに失敗しました'
}
